import { apiGet, apiPatch, apiPost } from '../../utilities/apiClient';
import {
  ADD_MESSAGES,
  FETCHING_MESSAGES,
  FETCHED_MESSAGES,
  PREPEND_MESSAGE,
  UPDATE_MESSAGE,
} from '../constants';
import { getSessionIds, getStoreState } from './_sessionIds';

export interface MessageRecipient {
  to_email: string;
  to_cognito: string | null;
  to_first: string | null;
  to_last: string | null;
}

export interface SendMessagePayload {
  subject: string;
  body: string;
  project_id?: number | string | null;
  attachments?: any[];
}

/**
 * Fetch all messages for a project. Skips if already fetched within the last
 * 60 seconds unless `override` is true. Mirrors web getMessages action.
 */
export const getMessages = (
  projectId: number | string,
  override = false,
) => async (dispatch: any): Promise<{ success: boolean; payload?: any[] }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId || projectId == null) return { success: false };

  const state = getStoreState() as any;
  const lastFetched = state.messages?.fetched?.[String(projectId)] ?? 0;
  const isFresh = Date.now() - lastFetched < 60_000;
  if (isFresh && !override) return { success: true, payload: state.messages?.list?.[String(projectId)] ?? [] };

  dispatch({ type: FETCHING_MESSAGES, payload: true });
  try {
    const res = await apiGet(`/messages/get-messages/${projectId}/${accountId}/${cognitoId}`);
    const list: any[] = Array.isArray(res?.payload ?? res) ? (res?.payload ?? res) : [];
    dispatch({ type: ADD_MESSAGES, payload: { project_id: projectId, list } });
    dispatch({ type: FETCHED_MESSAGES, payload: { project_id: projectId } });
    return { success: true, payload: list };
  } catch {
    dispatch({ type: FETCHING_MESSAGES, payload: false });
    return { success: false };
  }
};

/** Prepend a real-time message received via Ably (no API call needed). */
export const prependMessage = (
  projectId: number | string,
  message: any,
) => (dispatch: any) => {
  dispatch({ type: PREPEND_MESSAGE, payload: { project_id: projectId, message } });
};

/** Mark a message as read on the backend and update local state. */
export const markMessageRead = (
  messageId: string | number,
  projectId: number | string,
) => async (dispatch: any): Promise<{ success: boolean }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };
  try {
    // Backend (controllers/email/update-message/index.js) destructures
    // `{ updates }` from the body and forwards that object to
    // `database.updateById('messages', id, updates)`. We were sending
    // `{ read: true }` directly, which made `updates` undefined on the
    // server — the UPDATE silently failed, the optimistic local state
    // still flipped read=true, but the next refresh from the server
    // brought back read=false and the unread dot reappeared. Wrapping
    // matches the web client (frontend/client/src/store/actions/messages.js
    // → updateMessage) and the server contract.
    await apiPatch(`/messages/update/${messageId}/${accountId}/${cognitoId}`, {
      updates: { read: true },
    });
    dispatch({
      type: UPDATE_MESSAGE,
      payload: { project_id: projectId, message: { id: messageId, read: true } },
    });
    return { success: true };
  } catch {
    return { success: false };
  }
};

/**
 * Send an email message to one or more recipients via the backend messaging system.
 * Mirrors the web sendMessage action in frontend/client/src/store/actions/messages.js.
 */
export const sendMessage = (
  message: SendMessagePayload,
  recipients: MessageRecipient[],
  cc: MessageRecipient[] = [],
  options: { deliverAsPdf?: boolean } = {},
) => async (): Promise<{ success: boolean; message?: string; error?: string; payload?: any[] }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    const res = await apiPost(`/messages/send/${accountId}/${cognitoId}`, {
      message: { ...message, attachments: message.attachments ?? [] },
      recipients,
      cc,
      // The typed text goes as a PDF attachment under a short cover note: the
      // route out of a phi_requires_attachment refusal (utilities/sendAsPdf).
      ...(options.deliverAsPdf ? { deliver_as_pdf: true } : {}),
    });
    return res ?? { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};
